import { Layout as RaLayout, useGetIdentity, useLocaleState, useTranslate } from 'react-admin';
import { Typography, CircularProgress, Backdrop, Avatar } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BadgeNotificationProvider } from './contexts/BadgeNotificationContext';
import { useBranding } from './contexts/BrandingContext';
import { NotificationCenter } from './components/Notifications';
import ConsentGate from './components/ConsentGate';
import { CommandPalette, PaletteEntry } from './components/Search';
import { MaterialSymbol } from './components/common/MaterialSymbol';
import { shadeHexColor } from './utils/colorShade';
import { tokens } from './designTokens';
import { jwtDecode } from 'jwt-decode';
import tokenService from './services/tokenService';
import { SUPPORTED_LANGUAGES } from './i18n/languages';

const SSO_URL = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';

/** Menümodell: Drawer-Gruppen und Bottom-Nav speisen sich aus derselben Quelle
 *  (Kunden-Feedback: Schnellzugriff unten = gleiche Reihenfolge/Logik wie Burger-Menü). */
interface NavItem {
    label: string;
    icon: string;
    to: string;
    /** Aktiv-Erkennung: exakter Pfad oder Präfix(e) */
    match: (pathname: string) => boolean;
    visible: boolean;
    /** Auch im Bottom-Nav anzeigen */
    bottomNav?: boolean;
    /** Kurz-Label für den Bottom-Nav (Default: label) */
    bottomLabel?: string;
}

interface NavGroup {
    label: string;
    icon: string;
    items: NavItem[];
    /** Gruppe initial aufgeklappt */
    open?: boolean;
}

const HELP_URL = 'https://hilfe.generationl-onlineacademy.de/';
const HELP_LOCALES = ['en', 'fr', 'it', 'es', 'nl', 'pl'];

const CustomAppBar = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
    const [loadingLanguage, setLoadingLanguage] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [locale, setLocale] = useLocaleState();
    const navigate = useNavigate();
    const location = useLocation();
    const { data: identity } = useGetIdentity();
    const translate = useTranslate();
    const { branding } = useBranding();

    // Branding-Farben auf CSS-Custom-Properties spiegeln. Die --oa-*-Tokens
    // treiben das komplette Design-System-CSS (design-system.css); die
    // dark/light-Ableitungen kommen bei Fremd-Branding aus shadeHexColor,
    // beim Marken-Default aus den exakten Token-Werten (CI-Farben).
    useEffect(() => {
        const root = document.documentElement.style;
        const primary = branding.primaryColor;
        const secondary = branding.secondaryColor;
        root.setProperty('--oa-primary', primary);
        root.setProperty('--oa-primary-dark', primary === tokens.color.primary ? tokens.color.primaryDark : shadeHexColor(primary, -0.16));
        root.setProperty('--oa-primary-light', primary === tokens.color.primary ? tokens.color.primaryLight : shadeHexColor(primary, 0.35));
        root.setProperty('--oa-secondary', secondary);
        root.setProperty('--oa-secondary-dark', secondary === tokens.color.secondary ? tokens.color.secondaryDark : shadeHexColor(secondary, -0.2));
        root.setProperty('--oa-secondary-light', secondary === tokens.color.secondary ? tokens.color.secondaryLight : shadeHexColor(secondary, 0.2));
        // Legacy-Variablen für Bestands-CSS (Bootstrap-Hover, MUI-Button-Overrides)
        root.setProperty('--nav-secondary-color', secondary);
        root.setProperty('--branding-primary', primary);
        root.setProperty('--branding-primary-hover', primary + 'cc');
    }, [branding.primaryColor, branding.secondaryColor]);

    const logoSrc = branding.logoUrl
        ? (branding.logoUrl.startsWith('/') ? `${SSO_URL}${branding.logoUrl}` : branding.logoUrl)
        : '/images/top-bar-logo.png';

    // Get user data from identity (fetched from SSO userinfo endpoint)
    const firstName = (identity as any)?.firstName || '';
    const lastName = (identity as any)?.lastName || '';
    const email = (identity as any)?.email || identity?.fullName || 'user@example.com';
    const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || email.charAt(0).toUpperCase() || 'U';
    const displayName = firstName && lastName ? `${firstName} ${lastName}` : email;
    const avatar = (
        <Avatar src={(identity as any)?.avatar} alt="">
            {initials}
        </Avatar>
    );

    const handleNavigate = (path: string) => {
        navigate(path);
        setDrawerOpen(false);
    };

    const handleLanguageChange = async (newLocale: string) => {
        if (newLocale !== locale) {
            setLoadingLanguage(true);
            setLocale(newLocale);
            localStorage.setItem('locale', newLocale);
            // Wait for translations to load - give it more time for DeepL API
            await new Promise(resolve => setTimeout(resolve, 2000));
            setLoadingLanguage(false);
        }
        setLanguageMenuOpen(false);
    };

    const currentLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === locale);

    // Role-based visibility
    const getUserRoles = (): string[] => {
        const token = localStorage.getItem('token');
        if (!token) return [];
        try {
            const decoded: any = jwtDecode(token);
            const roles = decoded.roles || [];
            // Issue #267 defense-in-depth: roles kann (vor SSO-Fix) als
            // Object {"0":...,"2":...} kommen wenn array_unique Luecken
            // hinterlaesst. Object.values normalisiert das zu einem Array.
            if (Array.isArray(roles)) return roles;
            if (roles && typeof roles === 'object') return Object.values(roles) as string[];
            return [];
        } catch {
            return [];
        }
    };
    const userRoles = getUserRoles();
    const hasRole = (role: string) => userRoles.includes(role);
    const isInstructor = hasRole('ROLE_INSTRUCTOR');
    const isContentManager = hasRole('ROLE_CONTENT_MANAGER');
    const isCompanyAdmin = hasRole('ROLE_COMPANY_ADMIN');
    const isOperator = hasRole('ROLE_OPERATOR');
    const isPlatformAdmin = hasRole('ROLE_PLATFORM_ADMIN');
    const isMediaManager = hasRole('ROLE_MEDIA_MANAGER');
    // Can manage courses, enrollments, media
    const isAdminOrInstructor = isInstructor || isContentManager || isCompanyAdmin || isOperator || isPlatformAdmin;
    const helpChapter = isCompanyAdmin || isOperator || isPlatformAdmin ? 'administrator/'
        : isInstructor || isContentManager || isMediaManager ? 'lehrende/'
        : hasRole('ROLE_STUDENT') || hasRole('ROLE_USER') ? 'lernende/'
        : '';
    const helpUrl = `${HELP_URL}${HELP_LOCALES.includes(locale) ? `${locale}/` : ''}${helpChapter}`;
    // Issue #171: Mediathek separat — ROLE_MEDIA_MANAGER hat im Backend
    // vollen Zugriff (MediaFile/-Folder/-Category + Upload), darf den Eintrag
    // also auch sehen, ohne sonstige Admin-Rechte mitzubringen.
    const canSeeMediaLibrary = isAdminOrInstructor || isMediaManager;
    // Aus dem Admin verschobene Verwaltung (LMS-spezifisch):
    //  - isManager: Content-Manager aufwärts (Vorlagen, Lernpfade, Analytics, Bestellungen, Landing-Pages)
    //  - isCompanyLevel: Company-Admin aufwärts (lizenzierte Kurse, Payment)
    const isManager = isContentManager || isCompanyAdmin || isOperator || isPlatformAdmin;
    const isCompanyLevel = isCompanyAdmin || isOperator || isPlatformAdmin;
    const hasAnalyticsFeature = tokenService.hasFeature('reports.analytics');

    const { pathname } = location;
    const dashboardItem: NavItem = {
        label: translate('menu.dashboard'),
        icon: 'dashboard',
        to: '/',
        match: (p) => p === '/' || p === '/dashboard',
        visible: true,
        bottomNav: true,
    };

    const groups: NavGroup[] = [
        {
            label: 'Lernen',
            icon: 'school',
            open: true,
            items: [
                { label: translate('menu.myCourses'), icon: 'school', to: '/my-courses', match: (p) => p.startsWith('/my-courses'), visible: true, bottomNav: true },
                // Issue #274: Kurskatalog (Lerner-Ansicht) fuer ALLE sichtbar,
                // Kursverwaltung (unten, Verwaltung) nur fuer Admins/Instructors.
                { label: 'Kurskatalog', icon: 'explore', to: '/catalog', match: (p) => p === '/catalog', visible: true, bottomNav: true },
                { label: translate('menu.learningPaths'), icon: 'route', to: '/learning-paths', match: (p) => p.startsWith('/learning-paths'), visible: true, bottomNav: true, bottomLabel: translate('bottomNav.learningPaths') },
                { label: translate('menu.enrollments'), icon: 'fact_check', to: '/enrollments/manage', match: (p) => p.startsWith('/enrollments'), visible: isAdminOrInstructor, bottomNav: true },
                { label: translate('menu.achievements'), icon: 'military_tech', to: '/achievements', match: (p) => p.startsWith('/achievements'), visible: true, bottomNav: true, bottomLabel: translate('bottomNav.achievements') },
                { label: translate('menu.certificates'), icon: 'workspace_premium', to: '/my-certificates', match: (p) => p.startsWith('/my-certificates') || p.startsWith('/certificates'), visible: true },
                // Issue #347: Peer-Reviews muss auch fuer Lernende sichtbar sein,
                // deshalb im Lernen-Block statt unter Verwaltung.
                { label: translate('menu.peerReview'), icon: 'rate_review', to: '/peer-review', match: (p) => p.startsWith('/peer-review'), visible: true },
            ],
        },
        {
            label: 'Wissen & Medien',
            icon: 'article',
            items: [
                { label: translate('menu.wiki'), icon: 'article', to: '/wiki', match: (p) => p.startsWith('/wiki'), visible: true, bottomNav: true },
                { label: translate('menu.mediaLibrary'), icon: 'image', to: '/media-library', match: (p) => p.startsWith('/media-library'), visible: canSeeMediaLibrary, bottomNav: true, bottomLabel: translate('bottomNav.media') },
                { label: translate('menu.notes'), icon: 'description', to: '/notes', match: (p) => p.startsWith('/notes'), visible: true },
            ],
        },
        {
            label: 'Verwaltung',
            icon: 'verified_user',
            items: [
                { label: translate('menu.courseCatalogs'), icon: 'folder', to: '/courses/catalogs', match: (p) => p.startsWith('/courses'), visible: isAdminOrInstructor, bottomNav: true },
                { label: 'Pflichtaufgaben', icon: 'check_box', to: '/course_assignments', match: (p) => p.startsWith('/course_assignments'), visible: isAdminOrInstructor },
                { label: 'Lernpfade verwalten', icon: 'route', to: '/learning_paths', match: (p) => p.startsWith('/learning_paths'), visible: isManager },
                { label: 'Lerngruppen', icon: 'groups', to: '/learning-groups', match: (p) => p.startsWith('/learning-groups'), visible: isAdminOrInstructor },
                // Issue #197 (EU-KI-Act): Human-Oversight — Trainer pruefen KI-Bewertungen
                { label: 'KI-Bewertungen', icon: 'smart_toy', to: '/ai-grading-review', match: (p) => p.startsWith('/ai-grading-review'), visible: isAdminOrInstructor },
                { label: 'Auszeichnungen verwalten', icon: 'emoji_events', to: '/badges', match: (p) => p.startsWith('/badges'), visible: isManager },
                { label: 'Analytics & Reports', icon: 'bar_chart', to: '/analytics-management', match: (p) => p.startsWith('/analytics-management'), visible: isManager && hasAnalyticsFeature },
                { label: translate('menu.instructorAnalytics'), icon: 'insights', to: '/analytics/instructor', match: (p) => p.startsWith('/analytics/instructor'), visible: isAdminOrInstructor && hasAnalyticsFeature },
                { label: 'Dokumentenvorlagen', icon: 'description', to: '/templates', match: (p) => p.startsWith('/templates'), visible: isManager },
                // Aus dem Admin verschoben (2026-09, azubi:web-Feedback):
                // Einladungen + Branding haendelt jede App selbst.
                { label: 'Einladungen', icon: 'mail', to: '/invitations', match: (p) => p.startsWith('/invitations'), visible: isCompanyLevel },
                { label: 'Branding', icon: 'palette', to: '/branding-settings', match: (p) => p.startsWith('/branding-settings'), visible: isCompanyLevel },
            ],
        },
        {
            label: 'Vertrieb & Lizenzen',
            icon: 'shopping_cart',
            items: [
                { label: 'Bestellungen', icon: 'shopping_cart', to: '/orders', match: (p) => p.startsWith('/orders'), visible: isManager },
                { label: 'Produkte', icon: 'sell', to: '/products', match: (p) => p.startsWith('/products'), visible: isManager },
                { label: 'Landing-Pages', icon: 'web', to: '/landing-pages', match: (p) => p.startsWith('/landing-pages'), visible: isManager },
                { label: 'Marktplatz', icon: 'shopping_bag', to: '/marketplace', match: (p) => p.startsWith('/marketplace'), visible: isManager },
                { label: 'Lizenzierte Kurse', icon: 'card_membership', to: '/licensed-courses', match: (p) => p.startsWith('/licensed-courses'), visible: isCompanyLevel },
                { label: 'Zahlungs-Einstellungen', icon: 'credit_card', to: '/payment-settings', match: (p) => p.startsWith('/payment-settings'), visible: isCompanyLevel },
            ],
        },
    ];

    const visibleGroups = groups
        .map((g) => ({ ...g, items: g.items.filter((i) => i.visible) }))
        .filter((g) => g.items.length > 0);

    // Bottom-Nav: gleiche Reihenfolge wie Drawer (Dashboard + markierte Einträge)
    const bottomNavItems = [dashboardItem, ...visibleGroups.flatMap((g) => g.items)].filter((i) => i.bottomNav);

    // Seiten-Schnellzugriff der Command-Palette aus demselben Menümodell
    const paletteEntries: PaletteEntry[] = useMemo(
        () =>
            [dashboardItem, ...visibleGroups.flatMap((g) => g.items)].map((i) => ({
                label: i.label,
                icon: i.icon,
                type: 'Seite',
                to: i.to,
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [userRoles.join(','), locale],
    );

    const openPalette = useCallback(() => setPaletteOpen(true), []);
    const closePalette = useCallback(() => setPaletteOpen(false), []);

    // ⌘/Strg+K öffnet die Suche, Esc schließt Drawer/Palette
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setPaletteOpen((prev) => !prev);
            } else if (e.key === 'Escape') {
                setPaletteOpen(false);
                setDrawerOpen(false);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    const logoutToSsoDashboard = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('redirect_after_login');
        localStorage.removeItem('code_verifier');
        // Don't set user_logged_out flag - allows auto-login from SSO
        sessionStorage.clear();
        window.location.href = `${SSO_URL}/dashboard`;
    };

    const logoutCompletely = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('redirect_after_login');
        localStorage.removeItem('code_verifier');
        localStorage.setItem('user_logged_out', 'true');
        sessionStorage.clear();
        const logoutUrl = new URL(`${SSO_URL}/logout`);
        logoutUrl.searchParams.append('redirect_uri', window.location.origin);
        window.location.href = logoutUrl.toString();
    };

    const renderDrawerItem = (item: NavItem) => (
        <button
            key={item.to}
            className={`ditem${item.match(pathname) ? ' active' : ''}`}
            onClick={() => handleNavigate(item.to)}
            style={{ border: 0, background: item.match(pathname) ? undefined : 'none', textAlign: 'left' }}
        >
            <MaterialSymbol name={item.icon} size={20} />
            {item.label}
        </button>
    );

    return (
        <>
            {/* ===== App-Bar (Design-System: 72px, zentrierte ⌘K-Suche) ===== */}
            {/* position/zIndex kommen aus design-system.css (--oa-z-appbar), damit die
                Stapelreihenfolge von Drawer, Scrim, Modal und Palette an einer Stelle
                definiert ist und die Overlays nicht unter der App-Bar landen. */}
            <header className="appbar">
                <button
                    className="navbar-brand p-0 border-0 bg-transparent"
                    onClick={() => handleNavigate('/')}
                    style={{ cursor: 'pointer' }}
                >
                    <img className="logo" src={logoSrc} alt="online:academy" style={{ maxWidth: 200, objectFit: 'contain' }} />
                </button>

                <button className="search d-none d-md-flex" onClick={openPalette} aria-label="Suche öffnen">
                    <MaterialSymbol name="search" size={18} />
                    <span className="ph">Kurse, Aktuelles &amp; Wissen …</span>
                    <span className="kbd"><kbd>⌘</kbd><kbd>K</kbd></span>
                </button>

                <div className="right">
                    <NotificationCenter />
                    <button className="avatar-btn" aria-label={translate('layout.profile')} onClick={() => handleNavigate('/profile')}>
                        {avatar}
                    </button>
                    <button className="logout d-none d-sm-inline-flex" onClick={logoutToSsoDashboard}>
                        {translate('layout.logout')}
                        <MaterialSymbol name="logout" size={18} />
                    </button>
                    <button className="burger" aria-label="Menü öffnen" onClick={() => setDrawerOpen(true)}>
                        <span></span><span></span><span></span>
                    </button>
                </div>
            </header>

            {/* ===== Drawer (Navy, aktiver Punkt = Marken-Vollbalken) ===== */}
            <div className={`scrim${drawerOpen ? ' open' : ''}`} onClick={() => setDrawerOpen(false)}></div>
            <aside className={`drawer${drawerOpen ? ' open' : ''}`} aria-hidden={!drawerOpen}>
                <div className="head">
                    <div className="av">{avatar}</div>
                    <div className="who">
                        <div className="n">{displayName}</div>
                        {firstName && lastName && <div className="e">{email}</div>}
                    </div>
                    <button className="close" aria-label="Menü schließen" onClick={() => setDrawerOpen(false)}>
                        <MaterialSymbol name="close" size={24} />
                    </button>
                </div>
                {/* Klasse heißt bewusst `dnav`, nicht `nav`: Bootstrap definiert `.nav`
                    global als `display:flex; flex-wrap:wrap` und würde die Menüeinträge
                    nebeneinander umbrechen statt untereinander stapeln. */}
                <nav className="dnav">
                    {renderDrawerItem(dashboardItem)}

                    {visibleGroups.map((group) => (
                        <details key={group.label} className="dgroup" open={group.open}>
                            <summary>
                                <MaterialSymbol name={group.icon} className="dlead" size={19} />
                                {group.label}
                                <MaterialSymbol name="chevron_right" className="chev" size={14} />
                            </summary>
                            <div className="glist">
                                {group.items.map(renderDrawerItem)}
                            </div>
                        </details>
                    ))}

                    <hr className="divider" />
                    <button
                        className="dlang"
                        onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                        style={{ border: 0, background: 'none', color: '#fff', cursor: 'pointer' }}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 14 }}>
                            <MaterialSymbol name="translate" size={20} />
                            {translate('layout.language')}
                        </span>
                        <span className="v">
                            <span className="badge">{locale?.toUpperCase()}</span>
                            {currentLanguage?.name}
                        </span>
                    </button>
                    {languageMenuOpen && (
                        <div className="glist">
                            {SUPPORTED_LANGUAGES.map((language) => (
                                <button
                                    key={language.code}
                                    className={`ditem${locale === language.code ? ' active' : ''}`}
                                    onClick={() => handleLanguageChange(language.code)}
                                    style={{ border: 0, background: locale === language.code ? undefined : 'none', textAlign: 'left', justifyContent: 'space-between' }}
                                >
                                    <span>{language.flag} {language.name}</span>
                                    {locale === language.code && <span>✓</span>}
                                </button>
                            ))}
                        </div>
                    )}
                </nav>
                <hr className="divider" />
                <div className="foot">
                    <button className="ditem" onClick={() => handleNavigate('/profile')} style={{ border: 0, background: 'none', textAlign: 'left' }}>
                        <MaterialSymbol name="person" size={20} />
                        {translate('layout.profile')}
                    </button>
                    <button className="ditem" onClick={() => handleNavigate('/change-password')} style={{ border: 0, background: 'none', textAlign: 'left' }}>
                        <MaterialSymbol name="key" size={20} />
                        {translate('layout.changePassword')}
                    </button>
                    <a className="ditem" href={helpUrl} target="_blank" rel="noopener noreferrer" onClick={() => setDrawerOpen(false)} style={{ textDecoration: 'none' }}>
                        <MaterialSymbol name="help" size={20} />
                        {translate('layout.help')}
                    </a>
                    <button className="logout" onClick={logoutCompletely}>
                        <MaterialSymbol name="logout" size={18} />
                        {translate('layout.logout')}
                    </button>
                </div>
            </aside>

            {/* ===== Bottom-Nav (Schnellzugriff, expandiert bei Hover) ===== */}
            <nav className="bottomnav">
                <div className="items">
                    {bottomNavItems.map((item) => (
                        <button
                            key={item.to}
                            className={`navitem${item.match(pathname) ? ' active' : ''}`}
                            onClick={() => handleNavigate(item.to)}
                            style={{ border: 0, background: item.match(pathname) ? undefined : 'none', fontFamily: 'inherit' }}
                        >
                            <MaterialSymbol name={item.icon} size={23} />
                            <span className="label">{item.bottomLabel || item.label}</span>
                        </button>
                    ))}
                </div>
                <span className="powered">powered by {branding.poweredByText || 'online:academy'}</span>
            </nav>

            {/* ===== Globale Suche (Command-Palette, ⌘K) ===== */}
            <CommandPalette open={paletteOpen} onClose={closePalette} pages={paletteEntries} />

            {/* Language Loading Indicator */}
            <Backdrop
                open={loadingLanguage}
                sx={{
                    color: '#fff',
                    zIndex: 9999,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }}
            >
                <div style={{ textAlign: 'center' }}>
                    <CircularProgress color="inherit" size={60} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        {translate('layout.loadingTranslations')}
                    </Typography>
                </div>
            </Backdrop>
        </>
    );
};

// Title Bar Component to show page title above content
const TitleBar = () => {
    const location = useLocation();

    // Map routes to titles
    const getTitleForRoute = (pathname: string): string => {
        if (pathname === '/' || pathname === '/dashboard') return 'Dashboard';
        if (pathname === '/catalog') return 'Kurskatalog';
        if (pathname.startsWith('/my-courses')) return 'Meine Kurse';
        if (pathname.startsWith('/courses')) return 'Kursverwaltung';
        if (pathname.startsWith('/learning-paths')) return 'Lernpfade';
        if (pathname.startsWith('/course_assignments')) return 'Pflichtaufgaben';
        if (pathname.startsWith('/learning_paths')) return 'Lernpfade verwalten';
        if (pathname.startsWith('/badges')) return 'Auszeichnungen verwalten';
        if (pathname.startsWith('/templates')) return 'Dokumentenvorlagen';
        if (pathname.startsWith('/achievements')) return 'Meine Erfolge';
        if (pathname.startsWith('/my-certificates') || pathname.startsWith('/certificates')) return 'Meine Zertifikate';
        if (pathname === '/enrollments/manage') return 'Einschreibungen verwalten';
        if (pathname === '/enrollments/dashboard') return 'Einschreibungs-Übersicht';
        if (pathname.startsWith('/enrollments')) return 'Einschreibungen';
        if (pathname.startsWith('/media-library')) return 'Medienbibliothek';
        if (pathname.startsWith('/wiki')) return 'Aktuelles & Wissen';
        if (pathname.startsWith('/profile')) return 'Profil';
        if (pathname.startsWith('/change-password')) return 'Passwort ändern';
        return '';
    };

    const title = getTitleForRoute(location.pathname);

    if (!title) return null;

    return (
        <Typography
            variant="h1"
            component="h1"
            style={{
                fontSize: '2.375rem',
                fontWeight: 700,
                lineHeight: 1.2,
                margin: '4px 0 22px',
                color: 'var(--oa-text)',
            }}
        >
            {title}
        </Typography>
    );
};

// Main Layout Component
export const Layout = (props: any) => {
    return (
        <BadgeNotificationProvider>
            <RaLayout
                {...props}
                appBar={CustomAppBar}
                menu={() => null} // Remove default sidebar
                sx={{
                    '& .RaLayout-appFrame': {
                        marginTop: '72px', // Space for fixed 72px app bar
                    },
                    '& .RaLayout-content': {
                        padding: 0,
                        marginLeft: '0 !important',
                        backgroundColor: 'var(--oa-paper)',
                    },
                    '& .RaSidebar-root': {
                        display: 'none',
                    },
                    '& .RaSidebar-docked': {
                        display: 'none',
                    },
                    '& .RaAppBar-menuButton': {
                        display: 'none',
                    },
                    '& #main-content': {
                        padding: '0 !important',
                    },
                }}
            >
                <div className="page">
                    <TitleBar />
                    {/* Blockiert bei offenen Rechtsdokument-Zustimmungen (AGB etc.) */}
                    <ConsentGate />
                    {props.children}
                </div>
            </RaLayout>
        </BadgeNotificationProvider>
    );
};

export default Layout;
